import {
  NavUserAvatar,
  NavUserNameTypography,
  NavUserNameWrapper,
  NavUserRoleTypography,
  NavUserWrapper,
  NavUserWrapperContent,
  NavUserWrapperContentAvatarWrapper
} from './styled';
import React, { useContext } from 'react';
import { SidebarNavUserAvatarProps, SidebarNavUserProps } from '../../props';

import AuthContext from '@/auth/application/AuthContext';
import { getCurrentVersion } from '@/common/application/utils/getCurrentVersion';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
import { useFeatureFlag } from '@/common/presentation/hooks/useFeatureFlag';
import { Chip } from '@mui/material';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { SidebarContext } from '../../context';
import { useNavigate } from 'react-router-dom';
import tid from '@/common/infrastructure/config/tid';

const getInitial = (s: string): string => {
  if (!s) return '';

  return s.charAt(0).toUpperCase();
};

const NavUser = ({ name, sidebarOpen, dbAlias }: SidebarNavUserAvatarProps & { dbAlias?: string }) => {
  return (
    <NavUserWrapperContentAvatarWrapper sidebarOpen={sidebarOpen}>
      <NavUserAvatar>{getInitial(name as string)}</NavUserAvatar>
      <NavUserNameWrapper
        sx={{
          display: sidebarOpen ? 'flex' : 'none'
        }}
      >
        <NavUserNameTypography title={name}>{name}</NavUserNameTypography>
        <NavUserRoleTypography title={dbAlias || ''}>{dbAlias || ''}</NavUserRoleTypography>
      </NavUserNameWrapper>
    </NavUserWrapperContentAvatarWrapper>
  );
};

const SidebarNavUser: React.FC<SidebarNavUserProps> = ({ name }: SidebarNavUserProps) => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const { sidebarOpen } = useContext(SidebarContext);

  const isMyAccountEnabled = useFeatureFlag(FEATURE_FLAGS.AUTH_MY_ACCOUNT_ENABLED);

  // Get user data from auth context
  const userFullName = authContext?.user?.fullName || name;
  const userDbAlias = authContext?.user?.dbAlias;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    if (authContext) {
      authContext.logout();
    }
    handleClose();
    navigate('/login');
  };

  return (
    <>
      <NavUserWrapper
        sidebarOpen={sidebarOpen}
        onClick={handleClick}
        id="sidenav-user-button"
        aria-haspopup="true"
        {...tid('common:layout:sidebar-nav-user:user-button')}
      >
        <NavUserWrapperContent sidebarOpen={sidebarOpen}>
          <Divider sx={{ borderColor: 'white' }} />
          {sidebarOpen && (
            <Chip
              label={`v${getCurrentVersion()}`}
              disabled
              size="small"
              sx={{ cursor: 'pointer', color: 'white', paddingTop: 2 }}
              {...tid('common:layout:sidebar-nav-user:version-chip')}
            />
          )}
          <NavUser name={userFullName} sidebarOpen={sidebarOpen} dbAlias={userDbAlias} />
        </NavUserWrapperContent>
      </NavUserWrapper>
      <Menu
        id="sidenav-user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'sidenav-user-button'
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        {...tid('common:layout:sidebar-nav-user:menu')}
      >
        {isMyAccountEnabled && (
          <MenuItem onClick={handleClose} {...tid('common:layout:sidebar-nav-user:my-account-menu-item')}>
            My account
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout} {...tid('common:layout:sidebar-nav-user:logout-menu-item')}>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default SidebarNavUser;
